import { useMemo } from 'react'
import {
  Input,
  NativeSelect,
  Textarea,
  Box,
  SimpleGrid,
  Text,
  VStack,
  HStack,
  IconButton,
  Button,
  Badge,
  Spinner,
  Field,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiGitBranch, FiArrowRight } from 'react-icons/fi'
import type {
  ScriptElementFormState,
  TagPickerDto,
  QuestionPickerDto,
  ConditionalBranchType,
  ScriptLane,
} from '../../types/script'
import {
  CONDITIONAL_BRANCH_TYPE_LABELS,
  ScriptElementType,
} from '../../types/script'
import { useQuestionAnswers, useAttributePicker } from '../../hooks/useScript'

interface ConditionalBranchElementFieldsProps {
  element: ScriptElementFormState
  allElements: ScriptElementFormState[]
  onChange: (updates: Partial<ScriptElementFormState>) => void
  tagOptions: TagPickerDto[]
  questionOptions: QuestionPickerDto[]
  onCreateBranch?: (branchType: string, branchKey: string, label: string) => void
  lanes?: ScriptLane[]
}

function BranchPathControl({
  label,
  branchType,
  branchKey,
  element,
  onCreateBranch,
  lanes,
  colorScheme = 'orange',
}: {
  label: string
  branchType: string
  branchKey: string
  element: ScriptElementFormState
  onCreateBranch?: (branchType: string, branchKey: string, label: string) => void
  lanes?: ScriptLane[]
  colorScheme?: string
}) {
  // Check if a lane already exists for this branch
  const existingLane = lanes?.find(
    (l) =>
      l.sourceElementClientId === element.clientId &&
      l.sourceBranchKey === branchKey
  )

  return (
    <Field.Root>
      <Field.Label fontSize="xs">{label}</Field.Label>
      {existingLane ? (
        <Badge colorPalette={existingLane.colorScheme} fontSize="xs" px={2} py={1}>
          <HStack gap={1}>
            <FiArrowRight size={10} />
            <Text>{existingLane.label} path</Text>
          </HStack>
        </Badge>
      ) : onCreateBranch ? (
        <Button
          size="xs"
          variant="outline"
          colorPalette={colorScheme}
          onClick={() => onCreateBranch(branchType, branchKey, label)}><FiGitBranch />Create Path
                  </Button>
      ) : (
        <Text fontSize="xs" color="gray.500" fontStyle="italic">
          Continue to next step
        </Text>
      )}
    </Field.Root>
  );
}

function AnswerConditionalFields({
  element,
  allElements,
  onChange,
  questionOptions,
  onCreateBranch,
  lanes,
}: {
  element: ScriptElementFormState
  allElements: ScriptElementFormState[]
  onChange: (updates: Partial<ScriptElementFormState>) => void
  questionOptions: QuestionPickerDto[]
  onCreateBranch?: (branchType: string, branchKey: string, label: string) => void
  lanes?: ScriptLane[]
}) {
  const priorQuestionElements = allElements.filter(
    (el) =>
      el.elementType === ScriptElementType.SurveyQuestion && el.surveyQuestionId
  )

  const selectedQuestionId = element.conditionalOperator || null
  const selectedAnswerId = element.conditionalValue || null

  const { data: questionAnswers, isLoading: answersLoading } =
    useQuestionAnswers(selectedQuestionId)

  const handleQuestionElementChange = (surveyQuestionId: string | null) => {
    onChange({
      conditionalOperator: surveyQuestionId || undefined,
      conditionalValue: undefined,
    })
  }

  return (
    <VStack gap={3} align="stretch">
      <Field.Root>
        <Field.Label fontSize="xs">Question Element</Field.Label>
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            value={selectedQuestionId ?? ''}
            onChange={(e) => handleQuestionElementChange(e.target.value || null)}
            placeholder="Select a survey question...">
            {priorQuestionElements.map((el) => {
              const q = questionOptions.find((qo) => qo.id === el.surveyQuestionId)
              return (
                <option key={el.clientId} value={el.surveyQuestionId!}>
                  {q?.label ?? `Question (Step ${allElements.findIndex((a) => a.clientId === el.clientId) + 1})`}
                </option>
              )
            })}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
        {priorQuestionElements.length === 0 && (
          <Text fontSize="xs" color="gray.400" fontStyle="italic" mt={1}>
            Add a Survey Question element to branch on answers.
          </Text>
        )}
      </Field.Root>

      {selectedQuestionId && (
        <Field.Root>
          <Field.Label fontSize="xs">Answer to Match</Field.Label>
          {answersLoading ? (
            <HStack py={1}>
              <Spinner size="xs" />
              <Text fontSize="xs" color="gray.500">Loading answers...</Text>
            </HStack>
          ) : (
            <NativeSelect.Root size="sm">
              <NativeSelect.Field
                value={selectedAnswerId ?? ''}
                onChange={(e) => onChange({ conditionalValue: e.target.value || undefined })}
                placeholder="Select an answer...">
                {questionAnswers?.answers.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.answerText}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          )}
        </Field.Root>
      )}

      <Text fontSize="xs" fontWeight="semibold" color="orange.600">
        Branching
      </Text>
      <SimpleGrid columns={2} gap={2}>
        <BranchPathControl
          label="If answer matches"
          branchType="true"
          branchKey="true"
          element={element}
          onCreateBranch={onCreateBranch}
          lanes={lanes}
        />
        <BranchPathControl
          label="If answer doesn't match"
          branchType="false"
          branchKey="false"
          element={element}
          onCreateBranch={onCreateBranch}
          lanes={lanes}
        />
      </SimpleGrid>
    </VStack>
  );
}

function TagConditionalFields({
  element,
  onChange,
  tagOptions,
  onCreateBranch,
  lanes,
}: {
  element: ScriptElementFormState
  onChange: (updates: Partial<ScriptElementFormState>) => void
  tagOptions: TagPickerDto[]
  onCreateBranch?: (branchType: string, branchKey: string, label: string) => void
  lanes?: ScriptLane[]
}) {
  const visibleTags = useMemo(
    () => tagOptions.filter((t) => t.isVisible),
    [tagOptions]
  )

  return (
    <VStack gap={3} align="stretch">
      <Field.Root>
        <Field.Label fontSize="xs">Tag</Field.Label>
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            value={element.tagId ?? ''}
            onChange={(e) => onChange({ tagId: e.target.value || null })}
            placeholder="Select a tag...">
            {visibleTags.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}{t.tagType ? ` (${t.tagType})` : ''}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
        {visibleTags.length === 0 && (
          <Text fontSize="xs" color="gray.400" fontStyle="italic" mt={1}>
            No tags available. Create tags in the Tags section first.
          </Text>
        )}
      </Field.Root>

      <Text fontSize="xs" fontWeight="semibold" color="orange.600">
        Branching
      </Text>
      <SimpleGrid columns={2} gap={2}>
        <BranchPathControl
          label="If contact HAS tag"
          branchType="true"
          branchKey="true"
          element={element}
          onCreateBranch={onCreateBranch}
          lanes={lanes}
        />
        <BranchPathControl
          label="If contact DOES NOT have tag"
          branchType="false"
          branchKey="false"
          element={element}
          onCreateBranch={onCreateBranch}
          lanes={lanes}
        />
      </SimpleGrid>
    </VStack>
  );
}

function AttributeConditionalFields({
  element,
  onChange,
  onCreateBranch,
  lanes,
}: {
  element: ScriptElementFormState
  onChange: (updates: Partial<ScriptElementFormState>) => void
  onCreateBranch?: (branchType: string, branchKey: string, label: string) => void
  lanes?: ScriptLane[]
}) {
  const { data: attributes = [] } = useAttributePicker()
  const selectedKey = element.conditionalOperator || ''
  const selected = attributes.find((a) => a.key === selectedKey)

  return (
    <VStack gap={3} align="stretch">
      <Field.Root>
        <Field.Label fontSize="xs">Attribute</Field.Label>
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            aria-label="Attribute"
            value={selectedKey}
            onChange={(e) => onChange({ conditionalOperator: e.target.value || undefined, conditionalValue: undefined })}
            placeholder="Select an attribute...">
            {attributes.map((a) => (
              <option key={a.key} value={a.key}>
                {a.label}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field.Root>

      {selected && (
        <Field.Root>
          <Field.Label fontSize="xs">Matches when</Field.Label>
          <NativeSelect.Root size="sm">
            <NativeSelect.Field
              aria-label="Match option"
              value={element.conditionalValue || ''}
              onChange={(e) => onChange({ conditionalValue: e.target.value || undefined })}
              placeholder="Select a value...">
              {selected.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Field.Root>
      )}

      <Text fontSize="xs" fontWeight="semibold" color="orange.600">
        Branching
      </Text>
      <SimpleGrid columns={2} gap={2}>
        <BranchPathControl
          label="If it matches"
          branchType="true"
          branchKey="true"
          element={element}
          onCreateBranch={onCreateBranch}
          lanes={lanes}
        />
        <BranchPathControl
          label="If it doesn't match"
          branchType="false"
          branchKey="false"
          element={element}
          onCreateBranch={onCreateBranch}
          lanes={lanes}
        />
      </SimpleGrid>
    </VStack>
  );
}

function PromptConditionalFields({
  element,
  onChange,
  onCreateBranch,
  lanes,
}: {
  element: ScriptElementFormState
  onChange: (updates: Partial<ScriptElementFormState>) => void
  onCreateBranch?: (branchType: string, branchKey: string, label: string) => void
  lanes?: ScriptLane[]
}) {
  const responses = element.promptResponses ?? []

  const handleAddResponse = () => {
    onChange({
      promptResponses: [...responses, { label: '', targetClientId: null }],
    })
  }

  const handleUpdateResponse = (
    index: number,
    label: string
  ) => {
    const updated = responses.map((r, i) =>
      i === index ? { ...r, label } : r
    )
    onChange({ promptResponses: updated })
  }

  const handleRemoveResponse = (index: number) => {
    onChange({ promptResponses: responses.filter((_, i) => i !== index) })
  }

  const getLaneForResponse = (responseLabel: string): ScriptLane | undefined => {
    if (!lanes) return undefined
    return lanes.find(
      (l) =>
        l.sourceElementClientId === element.clientId &&
        l.sourceBranchType === 'prompt-response' &&
        l.sourceBranchKey === responseLabel
    )
  }

  return (
    <VStack gap={3} align="stretch">
      <Field.Root>
        <Field.Label fontSize="xs">Prompt Text</Field.Label>
        <Textarea
          size="sm"
          rows={2}
          value={element.promptText ?? ''}
          onChange={(e) => onChange({ promptText: e.target.value })}
          placeholder="e.g., Is the voter home?"
        />
      </Field.Root>

      <Text fontSize="xs" fontWeight="semibold" color="orange.600">
        Responses
      </Text>

      {responses.map((response, index) => {
        const existingLane = response.label ? getLaneForResponse(response.label) : undefined

        return (
          <HStack key={index} gap={2} align="end">
            <Field.Root flex="1">
              {index === 0 && <Field.Label fontSize="xs">Response Label</Field.Label>}
              <Input
                size="sm"
                value={response.label}
                onChange={(e) =>
                  handleUpdateResponse(index, e.target.value)
                }
                placeholder="e.g., Yes"
              />
            </Field.Root>
            <Field.Root flex="1">
              {index === 0 && <Field.Label fontSize="xs">Branch to</Field.Label>}
              {existingLane ? (
                <Badge colorPalette={existingLane.colorScheme} fontSize="xs" px={2} py={1}>
                  <HStack gap={1}>
                    <FiArrowRight size={10} />
                    <Text>{existingLane.label} path</Text>
                  </HStack>
                </Badge>
              ) : onCreateBranch && response.label ? (
                <Button
                  size="xs"
                  variant="outline"
                  colorPalette="orange"
                  onClick={() =>
                    onCreateBranch('prompt-response', response.label, response.label)
                  }><FiGitBranch />Create Path
                                  </Button>
              ) : (
                <Text fontSize="xs" color="gray.500" fontStyle="italic" py={1}>
                  Continue to next
                </Text>
              )}
            </Field.Root>
            <IconButton
              aria-label="Remove response"
              size="sm"
              variant="ghost"
              colorPalette="red"
              onClick={() => handleRemoveResponse(index)}
              flexShrink={0}><FiTrash2 /></IconButton>
          </HStack>
        );
      })}

      <Button
        size="xs"
        variant="outline"
        onClick={handleAddResponse}
        alignSelf="flex-start"><FiPlus />Add Response
              </Button>
    </VStack>
  );
}

export function ConditionalBranchElementFields({
  element,
  allElements,
  onChange,
  tagOptions,
  questionOptions,
  onCreateBranch,
  lanes,
}: ConditionalBranchElementFieldsProps) {
  const branchType = (element.conditionalField as ConditionalBranchType) || ''

  const handleTypeChange = (newType: string) => {
    onChange({
      conditionalField: newType || undefined,
      conditionalOperator: undefined,
      conditionalValue: undefined,
      tagId: null,
      promptText: undefined,
      promptResponses: undefined,
      nextElementOnTrueClientId: null,
      nextElementOnFalseClientId: null,
    })
  }

  return (
    <Box bg="orange.50" p={3} borderRadius="md">
      <Field.Root mb={3}>
        <Field.Label fontSize="xs" fontWeight="semibold" color="orange.600">
          Conditional Type
        </Field.Label>
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            value={branchType}
            onChange={(e) => handleTypeChange(e.target.value)}
            placeholder="Select conditional type...">
            {(Object.entries(CONDITIONAL_BRANCH_TYPE_LABELS) as [ConditionalBranchType, string][]).map(
              ([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              )
            )}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field.Root>

      {branchType === 'Answer' && (
        <AnswerConditionalFields
          element={element}
          allElements={allElements}
          onChange={onChange}
          questionOptions={questionOptions}
          onCreateBranch={onCreateBranch}
          lanes={lanes}
        />
      )}

      {branchType === 'Tag' && (
        <TagConditionalFields
          element={element}
          onChange={onChange}
          tagOptions={tagOptions}
          onCreateBranch={onCreateBranch}
          lanes={lanes}
        />
      )}

      {branchType === 'Attribute' && (
        <AttributeConditionalFields
          element={element}
          onChange={onChange}
          onCreateBranch={onCreateBranch}
          lanes={lanes}
        />
      )}

      {branchType === 'Prompt' && (
        <PromptConditionalFields
          element={element}
          onChange={onChange}
          onCreateBranch={onCreateBranch}
          lanes={lanes}
        />
      )}

      {!branchType && (
        <Text fontSize="xs" color="gray.400" fontStyle="italic">
          Select a conditional type to configure branching logic.
        </Text>
      )}
    </Box>
  );
}
